import { Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SiFormModule } from '@siemens/element-ng/form';
import { Schedule, Track, Vehicle, Workspace } from 'src/app/main/shared/DataTypes';
import { SettingsService } from 'src/app/main/shared/services/settings.service';

@Component({
  selector: 'app-save-modal',
  imports: [FormsModule, SiFormModule, TranslateModule],
  templateUrl: './save-modal.component.html',
  styleUrl: './save-modal.component.scss'
})
export class SaveModalComponent implements OnInit {
  private settingsService = inject(SettingsService);

  readonly saveItemClicked = output<{
    name: string;
    version: string;
    description: string;
    workspaceId: number;
  }>();
  readonly renameItemClicked = output<{
    name: string;
    version: string;
    description: string;
  }>();

  ngOnInit(): void {
    const item = this.itemToSave();
    this.newItemName.set(item?.name ?? '');
    if (this.mode() === 'rename') {
      this.newItemVersion.set((item as Track)?.version ?? '');
      this.newItemDescription.set((item as Track)?.description ?? '');
    }
    if (this.availableWorkspaces().length > 0) {
      const defaultWorkspace = this.settingsService.resolveDefaultWorkspace(
        this.availableWorkspaces()
      );
      this.newItemWorkspace.set(defaultWorkspace ?? this.availableWorkspaces()[0]);
    }
  }

  readonly itemToSave = input.required<Track | Schedule | Vehicle | undefined>();
  readonly availableWorkspaces = input<Workspace[]>([]);
  readonly dataType = input.required<'track' | 'schedule' | 'vehicle'>();
  readonly mode = input<'save' | 'rename'>('save');

  readonly newItemName = signal('');
  readonly newItemDescription = signal('');
  readonly newItemVersion = signal('');
  readonly newItemWorkspace = signal<Workspace>(null!);

  /** Set when the item originates from workspaces that do not contain the selected target. */
  readonly sourceWorkspaceHint = computed<{ source: string; target: string } | undefined>(() => {
    if (this.mode() !== 'save' || this.dataType() === 'vehicle') return undefined;

    const sourceId = (this.itemToSave() as { id?: number } | undefined)?.id;
    const target = this.newItemWorkspace();
    if (!sourceId || !target) return undefined;

    const sourceWorkspaces = this.availableWorkspaces().filter(workspace =>
      this.containsItem(workspace, sourceId)
    );
    if (sourceWorkspaces.length === 0 || sourceWorkspaces.some(w => w.id === target.id)) {
      return undefined;
    }

    return { source: sourceWorkspaces.map(w => w.name).join(', '), target: target.name };
  });

  private containsItem(workspace: Workspace, itemId: number): boolean {
    const groups = this.dataType() === 'track' ? workspace.tracks : workspace.schedules;
    return (groups ?? []).some(group =>
      group.variants?.some((variant: Track | Schedule) => variant.id === itemId)
    );
  }

  saveNewItem() {
    if (this.mode() === 'rename') {
      this.renameItemClicked.emit({
        name: this.newItemName(),
        version: this.newItemVersion(),
        description: this.newItemDescription()
      });
      return;
    }
    const saveItemData = {
      name: this.newItemName(),
      version: this.newItemVersion(),
      description: this.newItemDescription(),
      workspaceId: this.newItemWorkspace()!.id!
    };
    this.saveItemClicked.emit(saveItemData);
  }

  generateName() {
    const itemToSave = this.itemToSave();
    let name = '';
    if (this.dataType() === 'track') {
      const track = itemToSave as Track;
      const firstStation = track.parameters!['station_data'][0].trackParameter1;
      const lastStation =
        track.parameters['station_data'][track.parameters['station_data'].length - 1]
          .trackParameter1;
      name = `${firstStation} - ${lastStation}`;
    } else if (this.dataType() === 'schedule') {
      const schedule = itemToSave as Schedule;
      const firstTrack = schedule.tracks[0];
      const lastTrack = schedule.tracks[schedule.tracks.length - 1];

      const firstStation = firstTrack.scheduleData.stoppingData[0].stationName;
      const lastStation =
        lastTrack.scheduleData.stoppingData[lastTrack.scheduleData.stoppingData.length - 1]
          .stationName;

      name = `${firstStation} - ${lastStation}`;
    }

    this.newItemName.set(name);
  }
}
